import React, { useState, useEffect } from 'react';
import LonjovialRadarChart from './LonjovialRadarChart';
import Questionario from './Questionario';
import LandingPage from './LandingPage';
import EbookViewer from './EbookViewer';
import { auth, googleProvider, db } from './firebaseConfig';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';

function App() {
  // --- ESTADO INICIAL ---
  // Verifica se já tem um #hash na URL ao carregar (ex: se der refresh na página do ebook)
  const getTelaInicial = () => {
    if (window.location.hash === '#ebook') return 'ebook';
    if (window.location.hash === '#diagnostico') return 'app';
    return 'landing';
  };

  const [telaAtual, setTelaAtual] = useState(getTelaInicial());
  
  const [relatorio, setRelatorio] = useState(null);
  const [mostrarPolitica, setMostrarPolitica] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [statusSalvo, setStatusSalvo] = useState("");
  const [respostasSalvas, setRespostasSalvas] = useState({});

  // --- SOLUÇÃO DO "BOTÃO VOLTAR" DO ANDROID ---
  useEffect(() => {
    const handlePopState = () => {
      // Quando o usuário aperta "Voltar" no celular/navegador:
      const hash = window.location.hash;
      
      if (hash === '#ebook') {
        setTelaAtual('ebook');
      } else if (hash === '#diagnostico') {
        setTelaAtual('app');
      } else {
        // Se não tem hash (voltou para o início), mostra a capa
        setTelaAtual('landing');
      }
    };

    // Escuta o evento de "voltar" do navegador
    window.addEventListener('popstate', handlePopState);
    
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // --- FUNÇÕES DE NAVEGAÇÃO QUE CRIAM HISTÓRICO ---
  const navegarParaEbook = () => {
    window.history.pushState({ tela: 'ebook' }, '', '#ebook');
    setTelaAtual('ebook');
  };

  const navegarParaApp = () => {
    window.history.pushState({ tela: 'app' }, '', '#diagnostico');
    setTelaAtual('app');
  };

  const voltarParaCapa = () => {
    // Em vez de mudar o estado direto, mandamos o navegador voltar.
    // O useEffect lá em cima vai detectar isso e mudar a tela.
    window.history.back(); 
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUsuario(user);
        setStatusSalvo("Conectado");
      } else {
        setUsuario(null);
        setStatusSalvo("");
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSalvarRelatorio = async () => {
    try {
      setStatusSalvo("Salvando...");
      let userAtual = usuario;
      
      if (!userAtual) {
        const result = await signInWithPopup(auth, googleProvider);
        userAtual = result.user;
      }

      await setDoc(doc(db, "usuarios", userAtual.uid), {
        email: userAtual.email,
        nome: userAtual.displayName,
        termosAceitos: true,
        dataAceite: new Date(),
        ultimaAtualizacao: new Date(),
        respostas: respostasSalvas 
      }, { merge: true });

      setStatusSalvo("✅ Salvo!");
      setTimeout(() => setStatusSalvo("Conectado"), 3000);

    } catch (error) {
      console.error("Erro ao salvar:", error);
      setStatusSalvo("Erro");
    }
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('relatorio-para-imprimir');
    if (!element) {
        console.error("Elemento não encontrado");
        return;
    }
    const opt = {
      margin: [5, 10, 5, 10], 
      filename: `Relatorio-Lonjovial-${new Date().toISOString().slice(0,10)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true }, 
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' }
    };
    html2pdf().set(opt).from(element).save();
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      setUsuario(null);
      setStatusSalvo("");
    });
  };

  const finalizarQuestionario = (resultado) => {
      setRelatorio(resultado);
      if (resultado.respostas) {
          setRespostasSalvas(resultado.respostas);
      }
  };

  const getNivelLonjovial = (nota) => {
    if (nota < 40) return { titulo: "Iniciante", corTexto: "text-red-600", corBarra: "bg-red-600", bgClaro: "bg-red-50", msg: "O início da jornada é agora." };
    if (nota < 60) return { titulo: "Aprendiz", corTexto: "text-orange-500", corBarra: "bg-orange-500", bgClaro: "bg-orange-50", msg: "Você já tem consciência, falta prática." };
    if (nota < 80) return { titulo: "Praticante", corTexto: "text-blue-600", corBarra: "bg-blue-600", bgClaro: "bg-blue-50", msg: "Muito bom! O equilíbrio está próximo." };
    return { titulo: "Mestre Lonjovial", corTexto: "text-green-600", corBarra: "bg-green-600", bgClaro: "bg-green-50", msg: "Excelente! Você é um exemplo de vitalidade." };
  };

  const nivelAtual = relatorio ? getNivelLonjovial(relatorio.indice) : null;

  const dataObjeto = relatorio?.data ? new Date(relatorio.data) : new Date();
  const dataFormatada = dataObjeto.toLocaleDateString('pt-BR', { 
      day: '2-digit', month: 'long', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
  });

  // --- CONTROLE DE TELAS ---

  // 1. Tela do EBOOK
  if (telaAtual === 'ebook') {
    return (
      <div className="relative">
        <EbookViewer />
        {/* Botão flutuante COM EFEITO VIDRO para voltar à Home */}
        <button 
          onClick={voltarParaCapa}
          className="fixed bottom-6 right-6 z-50 bg-slate-800/90 backdrop-blur-md text-white p-3 rounded-full shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2 text-sm font-bold pr-5 border border-white/10"
          title="Voltar para a Capa"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Sair do Livro
        </button>
      </div>
    );
  }

  // 2. Tela de LANDING (Capa)
  if (telaAtual === 'landing') {
    return <LandingPage 
              onIniciarDiagnostico={navegarParaApp} 
              onAbrirEbook={navegarParaEbook}
           />;
  }

  // 3. Tela do APP (Diagnóstico)
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center pb-10 font-sans relative">
      
      {/* HEADER DO APP */}
      <header className="relative w-full h-[350px] md:h-[450px] mb-10 shadow-2xl overflow-hidden flex items-center justify-center bg-blue-900">
        <img 
          src="/banner-bussola.png" 
          alt="Banner Bússola Caminho Lonjovial" 
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-900/60 to-blue-900/80 z-10"></div>
        
        {/* Botão de voltar para Home usando o histórico */}
        <button 
          onClick={voltarParaCapa}
          className="absolute top-4 left-4 z-30 text-blue-100 hover:text-white text-xs font-medium bg-blue-900/30 px-3 py-1 rounded-full backdrop-blur-sm transition-colors flex items-center gap-1 cursor-pointer"
        >
           <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
           Voltar para Home
        </button>

        <button 
          onClick={() => setMostrarPolitica(true)}
          className="absolute top-4 right-4 z-30 text-blue-100 hover:text-white text-xs font-medium transition-colors flex items-center gap-1 bg-blue-900/30 px-3 py-1 rounded-full backdrop-blur-sm cursor-pointer"
          title="Ler Termos e Privacidade"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.956 11.956 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Privacidade e Termos
        </button>

        <div className="relative z-20 text-center max-w-4xl px-4 text-white mt-8">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 tracking-tight drop-shadow-2xl text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-100">
            Diagnóstico Lonjovial
          </h1>
          <p className="text-xl md:text-3xl font-medium drop-shadow-lg opacity-95 text-blue-50">
            Sua jornada de autoconhecimento começa aqui.
          </p>
        </div>
      </header>

      {/* ... (RESTANTE DO CÓDIGO PERMANECE IGUAL) ... */}
      <main className="w-full max-w-5xl px-4">
        {!relatorio ? (
          <Questionario 
            aoFinalizar={finalizarQuestionario} 
            onAbrirTermos={() => setMostrarPolitica(true)}
            valoresIniciais={respostasSalvas} 
          />
        ) : (
          <div className="animate-fade-in-up space-y-8 -mt-8 relative z-30">
            
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200 shadow-sm p-4 rounded-xl flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                <div>
                    <h3 className="font-bold text-gray-700">Resultado Gerado</h3>
                    <p className="text-xs text-green-600 font-bold">{statusSalvo}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={handleDownloadPDF} className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-bold hover:bg-gray-200 shadow-sm border border-gray-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        PDF
                    </button>

                    {!usuario ? (
                        <button onClick={handleSalvarRelatorio} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-blue-700 shadow-md">
                            Salvar Resultado
                        </button>
                    ) : (
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                            {usuario.photoURL ? <img src={usuario.photoURL} alt="User" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">{usuario.displayName?.charAt(0)}</div>}
                            <span className="text-xs text-gray-800 font-bold max-w-[80px] truncate hidden md:block">{usuario.displayName?.split(' ')[0]}</span>
                            <button onClick={handleSalvarRelatorio} className="ml-2 p-2 bg-white rounded-full text-blue-600 hover:text-blue-800 shadow border border-gray-100" title="Salvar agora"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-4 4m0 0l-4-4m4 4V4" /></svg></button>
                            <button onClick={handleLogout} className="ml-1 p-2 bg-red-50 rounded-full text-red-500 hover:bg-red-100 border border-red-100" title="Sair"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg></button>
                        </div>
                    )}
                </div>
            </div>

            <div id="relatorio-para-imprimir" className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100">
              
              <div className="flex flex-col md:flex-row justify-between items-center border-b border-gray-100 pb-6 mb-6 gap-6">
                <div className="text-center md:text-left flex-1">
                  <h2 className="text-3xl font-bold text-gray-800">Seu Relatório Lonjovial</h2>
                  <p className="text-gray-500 mt-2">Baseado nas 6 dimensões da vitalidade.</p>
                  <p className="text-sm text-gray-500 mt-2 font-medium bg-gray-50 inline-block px-2 py-1 rounded">
                    Gerado em: {dataFormatada}
                  </p>
                </div>

                <div className="flex flex-col items-center md:items-end gap-4 flex-1">
                  <div className="text-center md:text-right">
                    <p className="text-sm text-gray-400 font-bold uppercase tracking-wider">Índice Geral</p>
                    <p className={`text-2xl font-extrabold ${nivelAtual.corTexto} leading-none mt-1`}>
                      {nivelAtual.titulo}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center w-full max-w-[200px]">
                    <div className="relative w-28 h-28 md:w-32 md:h-32 flex items-center justify-center bg-gray-50 rounded-full border-4 border-white shadow-lg ring-4 ring-gray-100/50">
                      <span className={`text-3xl md:text-4xl font-extrabold ${nivelAtual.corTexto}`}>
                        {relatorio.indice}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-8 -mt-2 px-4 md:px-12">
                 <div className="relative h-4 w-full bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div 
                        className={`h-full ${nivelAtual.corBarra} transition-all duration-1000 ease-out relative`}
                        style={{ width: `${relatorio.indice}%` }}
                    >
                        <div className="absolute right-0 top-0 h-full w-2 bg-white/30 blur-[2px]"></div>
                    </div>
                 </div>
                 <div className="flex justify-between text-xs text-gray-400 mt-2 font-bold uppercase tracking-wider px-1">
                    <span>0% Iniciante</span>
                    <span>50%</span>
                    <span>100% Mestre</span>
                 </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className={`${nivelAtual.bgClaro} p-5 rounded-xl border-l-4 ${nivelAtual.corTexto.replace('text', 'border')} shadow-sm`}>
                    <h3 className={`font-bold text-lg mb-2 ${nivelAtual.corTexto}`}>Diagnóstico:</h3>
                    <p className="italic text-gray-700 text-lg font-medium leading-relaxed">"{nivelAtual.msg}"</p>
                  </div>
                  
                  <div className="mt-4">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Prioridades e Perdas
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">Veja onde você está bem e onde os maus hábitos (vermelho) estão consumindo sua energia:</p>
                    
                    <div className="space-y-4">
                      {[...relatorio.grafico]
                        .sort((a, b) => a.notaAtual - b.notaAtual)
                        .map((item, index) => {
                          let corBarraIndividual = 'bg-green-500';
                          if (item.notaAtual < 50) corBarraIndividual = 'bg-red-500';
                          else if (item.notaAtual < 75) corBarraIndividual = 'bg-orange-400';

                          return (
                            <div key={item.dimensao} className="relative">
                              <div className="flex justify-between text-sm font-bold mb-1">
                                <span className="flex items-center gap-2 text-gray-700">
                                  {index + 1}. {item.dimensao}
                                </span>
                                <span className="text-gray-900">{item.notaAtual}%</span>
                              </div>
                              
                              <div className="w-full bg-gray-100 rounded-full h-3 flex overflow-hidden shadow-sm">
                                <div 
                                  className={`h-full ${corBarraIndividual}`}
                                  style={{ width: `${item.notaAtual}%` }}
                                ></div>
                                
                                {item.perdaDetrator > 0 && (
                                    <div 
                                      className="h-full bg-red-600" 
                                      style={{ 
                                          width: `${item.perdaDetrator}%`,
                                          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.3) 5px, rgba(255,255,255,0.3) 10px)'
                                      }}
                                      title={`Você perdeu ${item.perdaDetrator}% devido aos hábitos nocivos`}
                                    ></div>
                                )}
                              </div>
                              
                              {item.perdaDetrator > 0 && (
                                  <p className="text-[10px] text-red-600 font-bold text-right mt-1">
                                    -{item.perdaDetrator}% por hábitos nocivos
                                  </p>
                              )}
                            </div>
                          );
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center justify-start">
                  <LonjovialRadarChart dadosExternos={relatorio.grafico} />
                  
                  <div className="mt-8 p-5 bg-blue-50 border border-blue-100 rounded-xl text-center shadow-sm w-full max-w-sm">
                    <p className="text-sm text-blue-900 font-bold mb-2 flex justify-center items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Como ler seu Radar
                    </p>
                    <p className="text-xs text-blue-700 leading-relaxed">
                        Este gráfico mostra a <strong>simetria</strong> da sua vida. 
                        Quanto mais "cheio" e redondo for o desenho, maior é o seu equilíbrio lonjovial. Pontas retraídas indicam oportunidades de melhoria.
                    </p>
                  </div>

                  <button 
                    onClick={handleDownloadPDF}
                    data-html2canvas-ignore="true" 
                    className="mt-8 w-full max-w-sm py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold transition-colors shadow-lg flex items-center justify-center gap-2 mb-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Baixar Relatório (PDF)
                  </button>

                  <button 
                    onClick={() => setRelatorio(null)}
                    data-html2canvas-ignore="true" 
                    className="w-full max-w-sm py-3 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-sm font-bold transition-colors border border-gray-200 shadow-sm flex items-center justify-center gap-2"
                  >
                    <span>🔄</span> Refazer o Teste
                  </button>
                </div>

              </div>
            </div>
            
            <div className="text-center p-8 bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-3">Próximo Passo</h3>
              <p className="text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">Salve este relatório (baixe o PDF). Foque nas dimensões com menor pontuação e refaça este teste em <strong>3 meses</strong> para acompanhar sua evolução.</p>
            </div>
          </div>
        )}
      </main>

      <footer className="mt-16 pb-8 text-gray-400 text-sm text-center">
        <p>© 2025 Caminho Lonjovial - Autodiagnóstico 6x6</p>
        <button 
          onClick={() => setMostrarPolitica(true)}
          className="mt-2 text-blue-500 hover:text-blue-700 underline text-xs cursor-pointer bg-transparent border-none"
        >
          Política de Privacidade e Termos de Uso
        </button>
      </footer>

      {mostrarPolitica && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[110] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-8 relative animate-fade-in-up">
            <button 
              onClick={() => setMostrarPolitica(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">Política de Privacidade</h2>
            
            <div className="prose prose-sm text-gray-600 space-y-4">
              <p><strong>Última atualização:</strong> Dezembro de 2025</p>
              
              <p>O <strong>Caminho Lonjovial</strong> valoriza sua privacidade. Esta política descreve como coletamos e protegemos seus dados em conformidade com a Lei Geral de Proteção de Dados (LGPD).</p>

              <h3 className="text-lg font-bold text-gray-800 mt-4">1. Coleta de Dados</h3>
              <p>Coletamos apenas o necessário para o funcionamento do diagnóstico:</p>
              <ul className="list-disc pl-5">
                <li><strong>Identificação:</strong> Ao fazer login com o Google, recebemos seu nome, e-mail e foto de perfil para criar sua conta.</li>
                <li><strong>Dados de Saúde e Hábitos:</strong> As respostas fornecidas no questionário são armazenadas para gerar seu gráfico e acompanhar sua evolução.</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-800 mt-4">2. Finalidade</h3>
              <p>Seus dados são utilizados <strong>exclusivamente</strong> para:</p>
              <ul className="list-disc pl-5">
                <li>Calcular seu Índice Lonjovial e gerar o diagnóstico, o relatório e o gráfico radar da longevidade.</li>
                <li>Permitir que você recupere suas respostas em acessos futuros.</li>
              </ul>
              <p><strong>Não vendemos nem compartilhamos seus dados pessoais com terceiros</strong> para fins publicitários.</p>

              <h3 className="text-lg font-bold text-gray-800 mt-4">3. Armazenamento e Segurança</h3>
              <p>Utilizamos a infraestrutura do <strong>Google Firebase</strong>, um dos serviços de nuvem mais seguros do mundo. Seus dados são armazenados de forma criptografada e o acesso administrativo é restrito.</p>

              <h3 className="text-lg font-bold text-gray-800 mt-4">4. Seus Direitos</h3>
              <p>Você tem total controle sobre suas informações. A qualquer momento, você pode solicitar a exclusão completa de todos os seus dados do nosso sistema entrando em contato com a administração do projeto.</p>
            </div>

            <div className="border-t border-gray-200 my-8 pt-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Termos de Uso</h2>
                
                <div className="prose prose-sm text-gray-600 space-y-4">
                    
                    <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-red-900">
                        <h3 className="text-lg font-bold text-red-800 mt-0 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            Isenção de Responsabilidade
                        </h3>
                        <p className="text-sm mt-2 font-bold">
                            Este app é educativo. Os resultados apresentados NÃO constituem diagnóstico médico, psicológico ou nutricional. Nunca altere sua saúde baseando-se apenas neste teste.
                        </p>
                    </div>

                    <h3 className="text-lg font-bold text-gray-800 mt-4">1. Natureza do Serviço</h3>
                    <p>Ao utilizar este aplicativo, você entende e concorda que a precisão dos resultados depende inteiramente da honestidade e subjetividade das suas respostas.</p>

                    <h3 className="text-lg font-bold text-gray-800 mt-4">2. Propriedade Intelectual</h3>
                    <p>Todo o conteúdo, metodologia, textos, logotipos e gráficos do "Caminho Lonjovial" são de propriedade exclusiva dos seus criadores. É proibida a cópia, reprodução ou uso comercial sem autorização expressa.</p>

                    <h3 className="text-lg font-bold text-gray-800 mt-4">3. Conduta do Usuário</h3>
                    <p>Você concorda em utilizar a ferramenta apenas para fins legítimos e pessoais, não tentando acessar dados de terceiros ou comprometer a segurança da plataforma.</p>

                    <h3 className="text-lg font-bold text-gray-800 mt-4">4. Disponibilidade</h3>
                    <p>O serviço é fornecido "como está". Não garantimos que a plataforma estará disponível 100% do tempo sem interrupções para manutenção ou atualizações.</p>
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end">
              <button 
                onClick={() => setMostrarPolitica(false)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;